import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { AvailableTimes as MuiAvailableTimes } from '../styled';
import { useScheduleAvailableTimeGroupedByPeriod } from '../hooks';
import { formatPeriodLabel, formatTimeLabel } from '../../../../lib/dayjs';
import { saveSelectedDate } from '../../../../features/schedule/schedule-slice';
import { goTo } from '../../../../features/stepper/stepper-slice';
import { RootState } from '../../../../store';
import { customEventTracking } from '../../../../lib/gtm';
import { ChilHourLabel, ChipHour, ChipList } from './style';
import { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { IAppointments } from '../../../../services/availableTimes.service';
import AvailableAddress from '../AvailableAddress';

interface AvailableTimesProps {
  appointments: IAppointments;
  isLoading: boolean;
}

const AvailableTimes = ({ appointments, isLoading }: AvailableTimesProps) => {
  const dispatch = useDispatch();
  const { selectedDay, dateStart, events } = useSelector((state: RootState) => state.schedule);
  const { calendarsAvailables } = useSelector((state: RootState) => state.calendar);
  const [scheduleAvailableTimeGroupedByPeriod] = useScheduleAvailableTimeGroupedByPeriod(appointments, selectedDay);
  const [duration, setDuration] = useState<string>('');

  useEffect(() => {
    if (calendarsAvailables.length === 0) return;
    const duration = calendarsAvailables
      .map(item => item.durationEvent)
      .reduce((acc: number[], item: number) => {
        if (acc.includes(item)) {
          return acc;
        } else {
          acc.push(item);
          return acc;
        }
      }, []);
    const durationLabel =
      duration.length == 1 ? `${duration[0]} minutos` : `${Math.max(...duration)} a ${Math.min(...duration)} minutos`;
    setDuration(`duração do atendimento: ${durationLabel}`);
  }, [calendarsAvailables]);

  useEffect(() => {
    const timer = setTimeout(() => {
      customEventTracking.send({
        event: events,
        props: {
          day_available: selectedDay,
          available_times: `morning: ${
            scheduleAvailableTimeGroupedByPeriod?.morning?.availableList.length > 0 ? true : false
          }, afternoon: ${
            scheduleAvailableTimeGroupedByPeriod?.afternoon?.availableList.length > 0 ? true : false
          }, evening: ${scheduleAvailableTimeGroupedByPeriod?.evening?.availableList.length > 0 ? true : false}`
        }
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [scheduleAvailableTimeGroupedByPeriod, events]);

  return (
    <MuiAvailableTimes>
      {!isLoading ? <></> : <CircularProgress sx={{ color: 'brand.darkGreen' }} />}
      <>
        {Object.keys(scheduleAvailableTimeGroupedByPeriod).map((key, index) => {
          return (
            scheduleAvailableTimeGroupedByPeriod[key].availableList.length >= 1 && (
              <Box key={index} data-cy={`schedule-period-${key}`} sx={{ marginBottom: { xs: '24px', md: '30px' } }}>
                <ChilHourLabel variant="body2R">{formatPeriodLabel(key)}</ChilHourLabel>
                <ChipList>
                  {scheduleAvailableTimeGroupedByPeriod[key].availableList.map((value, index) => {
                    return (
                      <ChipHour
                        data-cy={`schedule-hour-${index}`}
                        key={index}
                        label={formatTimeLabel(value)}
                        variant="outlined"
                        className={`${value === dateStart ? 'selected' : ''}`}
                        onClick={() => {
                          dispatch(saveSelectedDate(value));
                          dispatch(goTo('/acesso-rapido'));
                          customEventTracking.send({
                            event: 'calendar_touch_time',
                            props: {
                              touch_time: value
                            }
                          });
                        }}
                      />
                    );
                  })}
                </ChipList>
              </Box>
            )
          );
        })}
        <AvailableAddress appointments={appointments} />
        <Typography variant="body2R">{duration}</Typography>
      </>
    </MuiAvailableTimes>
  );
};

export default AvailableTimes;
